/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// TODO: Refactor page - remove inline styles, keep it consistent with the rest of the app's theme

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Form, Input, Popconfirm, Skeleton, Space, Switch, Table, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ScheduledReportFormModal from '../Modals/ScheduledReportFormModal';
import Highlighter from 'react-highlight-words';
import BotAvatar from '../UI/BotAvatar';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatorySearchParams, useObservatoryTenantsFilterState } from '../UI/ObservatoryFiltersRow';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { useDeleteScheduledReportMutation, useFetchScheduledReportsQuery, useUpdateScheduledReportMutation } from '../../store/scheduledReports/scheduledReportsApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import is from '../../utils/is';

const ScheduledReportsTable = () => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleMouseDown, isDragging } = useDragDetection();
  const observatorySearchParams = useObservatorySearchParams();
  const initialTenantFilters = useInitialTenantFilters();
  const { shouldSkipTenantsFilter } = useObservatoryTenantsFilterState(observatorySearchParams);
  const [searchParams] = useSearchParams();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: scheduledReports, isFetching: isFetchingScheduledReports } = useFetchScheduledReportsQuery({ orgId, urlParams: {
    ...observatorySearchParams, // just tenantsFilter in this case
    page: currentPage -1,
    pageSize: pageSize,
    sortBy: searchParams.get('sortBy') || 'name',
    order: searchParams.get('order') || 'asc',
    ...(searchParams.get('searchText') && { searchText: searchParams.get('searchText') }),
    ...(searchParams.get('searchRecipients') && { searchRecipients: searchParams.get('searchRecipients') }),
    ...(searchParams.get('enabled') && { enabled: searchParams.get('enabled') })
  } }, { skip: !orgId || shouldSkipTenantsFilter });
  const [deleteScheduledReport] = useDeleteScheduledReportMutation();
  const [updateScheduledReport] = useUpdateScheduledReportMutation();
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });
  const [selectedItem, setSelectedItem] = useState(undefined);

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'ascend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'name';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  // On mount
  useEffect(() => {
    updateUrlSearchParams(
      Number(searchParams.get('page')) + 1,
      DEFAULT_PAGINATION_SIZE,
      { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' }
    );
  }, []);

  const updateUrlSearchParams = (
    current = 1,
    pageSize = DEFAULT_PAGINATION_SIZE,
    sorting = { name: 'asc' },
    filters = {
      searchRecipients: undefined,
      enabled: undefined
    }
  ) => {
    setCurrentPage(current);
    if (pageSize) {
      setPageSize(pageSize);
    }
    const urlSearchParams = new URLSearchParams(location.search);

    // Filters
    const searchText = form.getFieldValue('searchText');
    const searchRecipients = filters?.recipients;
    const enabled = filters?.status;

    if (searchText) {
      urlSearchParams.set('searchText', encodeURIComponent(searchText));
    } else {
      urlSearchParams.delete('searchText');
    }

    if (searchRecipients) {
      urlSearchParams.set('searchRecipients', encodeURIComponent(searchRecipients));
    } else {
      urlSearchParams.delete('searchRecipients');
    }

    if (!is.falsy(enabled)) {
      urlSearchParams.set('enabled', enabled);
    } else {
      urlSearchParams.delete('enabled');
    }

    // Pagination
    urlSearchParams.set('page', current - 1);

    if (pageSize) {
      urlSearchParams.set('pageSize', pageSize);
    }

    // Sorting
    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'name' && searchParams.get('order') === 'asc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting, filters);
  };

  return (
    <Fragment>
      <Form
        layout="inline"
        name="reportsFiltersForm"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          searchText: searchParams.get('searchText') ? decodeURIComponent(searchParams.get('searchText')) : ''
        }}
        onFieldsChange={() => updateUrlSearchParams()}
        form={form}
      >
        <ObservatoryFiltersRow
          form={form}
          rangePickerProps={{
            hidden: true
          }}
          agentsPickerProps={{
            agentsList: bots?.items || [],
            disabled: isFetchingScheduledReports || isFetchingBots,
            loading: isFetchingBots
          }}
        />

        <Space wrap align="center" className="mb-4 w-100 justify-content-between">
          <Form.Item
            name="searchText"
            noStyle
          >
            <Input
              placeholder={t('Search in contents')}
              prefix={<SearchOutlined className="text-secondary" />}
              size="large"
              allowClear
            />
          </Form.Item>

          <Button
            type="primary"
            size="large"
            disabled={!bots}
            icon={<PlusIcon />}
            onClick={() => setSelectedItem({})}
          >
            {t('Add Report')}
          </Button>
        </Space>
      </Form>

      <Table
        rowKey="id"
        size="small"
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(!isDragging()) {
              setSelectedItem(record);
            }
          }
        })}
        rowClassName="cursor-pointer"
        loading={isFetchingScheduledReports && !scheduledReports}
        dataSource={scheduledReports?.items || []}
        pagination={{
          size: 'default',
          total: scheduledReports?.total || 0,
          defaultCurrent: 1,
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => `${t('Total Reports')}: ${total}`
        }}
        onChange={handleTableChange}
        columns={[
          {
            key: 'name',
            title: t('Report Group Name'),
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            ellipsis: true,
            render: (value, record) => {
              return value ? (
                <Space>
                  <Highlighter
                    highlightClassName="bg-highlight p-0"
                    searchWords={[form.getFieldValue('searchText')]}
                    autoEscape
                    textToHighlight={value}
                  />
                </Space>
              ) : null;
            }
          },
          {
            key: 'bots',
            title: t('Agents'),
            responsive: ['sm'],
            dataIndex: 'tenants',
            ellipsis: true,
            render: (values) => {
              if (!bots && isFetchingBots) {
                return <Skeleton.Button active size="small" />;
              }
              if (!values || !values.length) {
                return '-';
              }

              const botsToShow = (bots?.items || []).filter(bot => values.includes(bot.id)) ?? [];

              return (
                <ResponsiveTagsList
                  values={
                    botsToShow.map((bot) => ({
                      icon: <BotAvatar avatarSize={24} bot={bot} showBotName={false} />,
                      label: bot.displayName,
                      value: bot.id
                    }))
                  }
                  maxTagCount={4}
                  tagRender={(tag) => {
                    const matchedTag = botsToShow.find(bot => bot.id === tag.value);
                    return (
                      <Tooltip overlayClassName="pointer-events-none" title={matchedTag.displayName}>
                        <BotAvatar avatarSize={24} bot={matchedTag} showBotName={false} className="mr-2" />
                      </Tooltip>
                    );
                  }}
                />
              );
            }
          },
          {
            key: 'recipients',
            title: t('Recipients'),
            dataIndex: 'recipients',
            /* filters: (usersThinList || []).map(recipient => ({
              text: recipient.email,
              value: recipient.email
            })),
            filteredValue: searchParams.get('searchRecipients') ? [decodeURIComponent(searchParams.get('searchRecipients'))] : null,
            filterIcon: useTableFilterIcon, */
            responsive: ['md'],
            ellipsis: true,
            render: (values) => {
              if (!values || !values.length) {
                return '-';
              }

              return (
                <ResponsiveTagsList
                  values={values.map((recipient, index) => ({
                    label: recipient,
                    value: index
                  }))}
                  maxTagCount={1}
                  tagRender={(tag) => {
                    return (
                      <Fragment>
                        <Typography.Text ellipsis className="mr-2">
                          {tag.label}
                        </Typography.Text>
                      </Fragment>
                    );
                  }}
                />
              );
            }
          },
          {
            key: 'frequency',
            title: t('Frequency'),
            width: 200,
            responsive: ['xl'],
            render: (_, record) => {
              const freq = [];
              if (record.weekly) {
                freq.push('Weekly');
              }
              if (record.monthly) {
                freq.push('Monthly');
              }
              return freq.length ? freq.join(', ') : '-';
            }
          },
          {
            key: 'status',
            title: t('Status'),
            /* filters: [
              {
                text: t('Enabled'),
                value: true
              },
              {
                text: t('Disabled'),
                value: false
              }
            ],
            onFilter: (value, record) => value ? record.enabled : !record.enabled,
            filterIcon: useTableFilterIcon, */
            width: 120,
            responsive: ['sm'],
            dataIndex: 'enabled',
            render: (value, record) => (
              <Space onClick={(e) => e.stopPropagation()}>
                <Popconfirm
                  title={`${t('Are you sure you want to')} ${value ? t('disable') : t('enable')} ${t('this report?')}`}
                  placement="topRight"
                  okText="Confirm"
                  onConfirm={async () => {
                    updateScheduledReport({
                      orgId, reportId: record.id, data: {
                        ...record,
                        tenants: bots.items.filter(bot => record.tenants.includes(bot.id)).map((bot)=> bot.id),
                        enabled: !value
                      }
                    });
                  }}
                >
                  <Switch
                    checked={value}
                  />
                </Popconfirm>
              </Space>
            )
          },
          {
            key: 'actions',
            title: t('Actions'),
            align: 'center',
            width: 114,
            render: (_, record) => (
              <Space onClick={(e) => e.stopPropagation()} wrap={false} align="center">
                <Tooltip
                  overlayClassName="pointer-events-none"
                  title={t('Edit report')}
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditIcon />}
                    onClick={() => setSelectedItem(record)}
                  />
                </Tooltip>
                <Tooltip
                  overlayClassName="pointer-events-none"
                  title={t('Delete report')}
                >
                  <Popconfirm
                    title={t('Are you sure you want to delete this report?')}
                    placement="topRight"
                    okText={t('Delete')}
                    onConfirm={async () => {
                      deleteScheduledReport({ orgId, reportId: record.id });
                    }}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      icon={<DeleteIcon />}
                    />
                  </Popconfirm>
                </Tooltip>
              </Space>
            )
          }
        ]}
      />

      <ScheduledReportFormModal
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    </Fragment>
  );
};

export default ScheduledReportsTable;
